import { useGSAP } from '@gsap/react';
import { gsap } from 'gsap';
import React from 'react';
import { Button, Image } from 'react-bootstrap';
import { useRecoilState, useRecoilValue } from 'recoil';
import {
  backgroundThemeState,
  contactButtonHoverState,
  darkModeState,
  enterState,
  fontFamilyState,
  parentIdState,
} from '../../atoms';
import { changeFont } from '../../util';
import { fadeIn, scaleDown, scaleUp } from './custom/animations';
import { FontButton } from './custom/font/FontButton';
import { BackgroundImage } from './custom/theme/switch/BackgroundImage';
import { InvertFilter } from './custom/theme/switch/InvertFilter';
import { Switch } from './custom/theme/switch/Switch';
import classNames from './Enter.module.scss';
import { Iframe } from './Iframe';
import { WelcomeMessage } from './WelcomeMessage';

const invertFilterStyle: Record<string, string> = {
  position: 'absolute',
  top: '0',
  left: '0',
  filter: 'invert(100%)',
};

export const Enter: React.FC<React.PropsWithChildren<unknown>> = ({
  children,
}) => {
  const welcomeMessageRef = React.useRef(null);
  const background = useRecoilValue(backgroundThemeState);
  const [id, setId] = useRecoilState(parentIdState);
  const [access, setAccess] = useRecoilState(enterState);
  const [fontFace, setFontFace] = useRecoilState(fontFamilyState);

  const iframe = useRecoilValue(contactButtonHoverState);
  const isIframe = iframe === 'iframe';
  const [enter, setEnter] = React.useState(false);

  const theme = useRecoilValue(darkModeState);
  const handleAccess = () => setAccess(!access);

  const selectedTheme = !theme
    ? classNames.videoContainer
    : classNames.videoContainerDark;

  const handleFontChange = React.useCallback(
    ({ target }: any) => {
      setId(target.name);
      setFontFace(target.id);
      changeFont(target.name, target.id);
    },
    [setFontFace, setId]
  );

  const handleEnter = () => {
    handleEnterPortfolio();
    handleEnterButtonHover();
  };

  const handleEnterButtonHover = () =>
    gsap.fromTo(`.${classNames.enterBtn}`, scaleDown, scaleUp);

  const handleExit = () => gsap.to(`.${classNames.enterBtn}`, scaleDown);

  React.useEffect(() => {
    changeFont(id, fontFace);
  }, [fontFace, id]);

  useGSAP(
    () => {
      gsap.from('.welcome', fadeIn);

      return () => {
        gsap.killTweensOf('.welcome');
        gsap.killTweensOf(`.${classNames.enterBtn}`);
      };
    },
    {
      scope: welcomeMessageRef,
      dependencies: [!access, background],
      revertOnUpdate: true,
    }
  );

  const handleEnterPortfolio = React.useCallback(() => {
    setEnter(true);
    setTimeout(() => {
      setEnter(false);
    }, 1000);
  }, []);

  return (
    <div id={selectedTheme} className={selectedTheme}>
      {access ? (
        <div>
          <div
            id="blur"
            style={{
              position: 'absolute',
              width: '100%',
              height: '100%',
              background: 'rgba(0, 0, 0, 0.3882352941)',
            }}
          ></div>
          <BackgroundImage
            enter={enter}
            className="xmg"
            style={{
              position: 'absolute',
              top: '0',
              left: '0',
              filter: 'blur(2px)',
            }}
          />
        </div>
      ) : !theme ? (
        <InvertFilter
          className="filter"
          inEnterState={false}
          access={access}
          style={invertFilterStyle}
        />
      ) : (
        <BackgroundImage
          enter={enter}
          className="img"
          style={{ position: 'absolute', top: '0', left: '0' }}
        />
      )}

      <Switch isEnterState={access} />

      <FontButton
        className={classNames}
        selectedTheme={selectedTheme}
        handleFontChange={handleFontChange}
      />

      {!access && (
        <>
          {isIframe ? (
            <Iframe />
          ) : (
            <React.Fragment>
              <WelcomeMessage {...{ classNames, welcomeMessageRef }} />

              {!theme && (
                <React.Fragment>
                  <InvertFilter
                    className="filter"
                    inEnterState={true}
                    access={access}
                    style={invertFilterStyle}
                  />
                </React.Fragment>
              )}
            </React.Fragment>
          )}
          <div className={classNames.enterBtnContainer}>
            <Button
              id="countdown"
              className={classNames.enterBtn}
              onClick={handleAccess}
              onMouseEnter={handleEnter}
              onMouseLeave={handleExit}
              //style={{ position: 'absolute', background: 'transparent' }}
            >
              <span>
                {isIframe ? (
                  ''
                ) : (
                  <Image
                    className={classNames.enterBtn}
                    src={`https://img.icons8.com/ios-filled/30/${
                      !theme ? '000000' : 'ffffff'
                    }/enter-2.png`}
                    alt="enter"
                  />
                )}
              </span>
            </Button>
          </div>
        </>
      )}
      {access && children}
    </div>
  );
};
